/**
 * Lecturas de «Campos y numeración» que comparten la pantalla de Ajustes
 * (`useCamposEstructura`) y la tarjeta del asistente (`aplicarCambios.ts`), con
 * el cliente de quien está (RLS).
 */
import type { SupabaseClient } from "@supabase/supabase-js";
import { leerCampo, type CampanaPeriodo, type CampoEstructura, type ValorEstructura } from "./camposEstructura";
import type { DatosEstructura } from "./planNumeracion";

// El cliente tipado de la app o uno de prueba: solo se usan from/select/eq/is.
type Sb = Pick<SupabaseClient, "from">;

export async function cargarCampos(sb: Sb, companyId: string): Promise<CampoEstructura[]> {
  const { data, error } = await sb.from("estructura_campos").select("*").eq("company_id", companyId);
  if (error) throw error;
  return ((data ?? []) as Record<string, unknown>[]).map(leerCampo).filter((c): c is CampoEstructura => !!c);
}

export async function cargarValores(sb: Sb, companyId: string): Promise<ValorEstructura[]> {
  const { data, error } = await sb
    .from("estructura_valores")
    .select("campo_id, entidad_id, periodo, valor, manual")
    .eq("company_id", companyId);
  if (error) throw error;
  return (data ?? []) as ValorEstructura[];
}

export async function cargarCampanas(sb: Sb, companyId: string): Promise<CampanaPeriodo[]> {
  const { data, error } = await sb.from("campaigns").select("id, name, location_id, start_date, end_date").eq("company_id", companyId);
  if (error) throw error;
  return (data ?? []) as CampanaPeriodo[];
}

export async function cargarDatosEstructura(sb: Sb, companyId: string): Promise<DatosEstructura> {
  const [f, n, s, c] = await Promise.all([
    sb.from("locations").select("id, name, site_type").eq("company_id", companyId),
    sb.from("greenhouses").select("id, name, location_id").eq("company_id", companyId),
    sb.from("blocks").select("id, name, greenhouse_id").eq("company_id", companyId).is("archived_at", null),
    sb.from("crop_cycles").select("id, block_id, start_date, end_date, crop_name").eq("company_id", companyId),
  ]);
  for (const r of [f, n, s, c]) if (r.error) throw r.error;
  type Fila = Record<string, unknown>;
  return {
    // Los almacenes no son fincas: no se numeran ni llevan campos.
    fincas: ((f.data ?? []) as Fila[]).filter((x) => x.site_type !== "warehouse").map((x) => ({ id: String(x.id), name: String(x.name ?? "") })),
    naves: ((n.data ?? []) as Fila[]).map((x) => ({ id: String(x.id), name: String(x.name ?? ""), location_id: (x.location_id as string | null) ?? null })),
    sectores: ((s.data ?? []) as Fila[]).map((x) => ({ id: String(x.id), name: String(x.name ?? ""), greenhouse_id: String(x.greenhouse_id) })),
    ciclos: ((c.data ?? []) as Fila[]).map((x) => ({
      id: String(x.id),
      block_id: String(x.block_id),
      start_date: String(x.start_date),
      end_date: (x.end_date as string | null) ?? null,
      crop_name: String(x.crop_name ?? ""),
    })),
  };
}
